import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Card, CardContent } from '../ui/card';
import { Separator } from '../ui/separator';
import {
  Cloud,
  HardDrive,
  ExternalLink,
  Save,
  Loader2,
  Check,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const IDRIVE_PARTNER_URL = 'https://console.idrivee2.com/partner/a2gdesigns';

export default function UserStorageSettings({ isOpen, onClose }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [allowUserBuckets, setAllowUserBuckets] = useState(true);
  const [settings, setSettings] = useState({
    custom_bucket_enabled: false,
    idrive_endpoint: '',
    idrive_access_key: '',
    idrive_secret_key: '',
    idrive_bucket: ''
  });

  useEffect(() => {
    if (isOpen) {
      fetchSettings();
    }
  }, [isOpen]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/user/storage-settings`);
      setSettings(response.data.user_settings || {
        custom_bucket_enabled: false,
        idrive_endpoint: '',
        idrive_access_key: '',
        idrive_secret_key: '',
        idrive_bucket: ''
      });
      setAllowUserBuckets(response.data.allow_user_buckets !== false);
    } catch (error) {
      console.error('Failed to fetch storage settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/user/storage-settings`, settings);
      toast.success('Storage settings saved successfully');
      onClose();
    } catch (error) {
      console.error('Failed to save storage settings:', error);
      toast.error('Failed to save storage settings');
    } finally {
      setSaving(false);
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#1a1a2e] border-[#2d2d44] max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#6D4AFF]/20 rounded-lg">
              <HardDrive className="w-5 h-5 text-[#6D4AFF]" />
            </div>
            <div>
              <DialogTitle className="text-white">Storage Settings</DialogTitle>
              <DialogDescription className="text-gray-400">
                Manage your email storage
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-8 h-8 text-[#6D4AFF] animate-spin" />
          </div>
        ) : (
          <div className="space-y-6 py-4">
            {/* Current Storage */}
            <Card className="bg-[#252540] border-[#2d2d44]">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-gray-400 text-sm">Storage Used</span>
                  <span className="text-white font-medium">
                    {formatBytes(user?.storage_used || 0)} / {formatBytes(user?.storage_limit || 0)}
                  </span>
                </div>
                <div className="w-full bg-[#1a1a2e] rounded-full h-2">
                  <div 
                    className="bg-[#6D4AFF] h-2 rounded-full transition-all"
                    style={{ 
                      width: `${Math.min(100, ((user?.storage_used || 0) / (user?.storage_limit || 1)) * 100)}%` 
                    }}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Get More Storage */}
            <div className="p-4 bg-gradient-to-r from-[#6D4AFF]/20 to-[#8B5CF6]/20 border border-[#6D4AFF]/30 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">Need more storage?</p>
                  <p className="text-sm text-gray-400">Get iDrive E2 cloud storage</p>
                </div>
                <Button
                  onClick={() => window.open(IDRIVE_PARTNER_URL, '_blank')}
                  className="bg-[#6D4AFF] hover:bg-[#5B3FD9]"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Get Storage
                </Button>
              </div>
            </div>

            {allowUserBuckets && (
              <>
                <Separator className="bg-[#2d2d44]" />

                {/* Custom Bucket Settings */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-white font-medium">Use Custom Storage Bucket</p>
                      <p className="text-sm text-gray-400">Connect your own iDrive E2 bucket</p>
                    </div>
                    <Switch
                      checked={settings.custom_bucket_enabled}
                      onCheckedChange={(checked) => setSettings({ ...settings, custom_bucket_enabled: checked })}
                    />
                  </div>

                  {settings.custom_bucket_enabled && (
                    <div className="space-y-4 p-4 bg-[#252540] rounded-lg">
                      <div className="flex items-start gap-2 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                        <AlertCircle className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-blue-300">
                          Enter your iDrive E2 credentials. Your attachments will be stored in your own bucket.
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-gray-300">Endpoint URL</Label>
                        <Input
                          value={settings.idrive_endpoint}
                          onChange={(e) => setSettings({ ...settings, idrive_endpoint: e.target.value })}
                          className="bg-[#1a1a2e] border-[#2d2d44] text-white"
                          placeholder="https://e2.idrivee2.com"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-300">Bucket Name</Label>
                        <Input
                          value={settings.idrive_bucket}
                          onChange={(e) => setSettings({ ...settings, idrive_bucket: e.target.value })}
                          className="bg-[#1a1a2e] border-[#2d2d44] text-white"
                          placeholder="my-bucket-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-300">Access Key</Label>
                        <Input
                          value={settings.idrive_access_key}
                          onChange={(e) => setSettings({ ...settings, idrive_access_key: e.target.value })}
                          className="bg-[#1a1a2e] border-[#2d2d44] text-white"
                          placeholder="Your access key"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-300">Secret Key</Label>
                        <Input
                          type="password"
                          value={settings.idrive_secret_key}
                          onChange={(e) => setSettings({ ...settings, idrive_secret_key: e.target.value })}
                          className="bg-[#1a1a2e] border-[#2d2d44] text-white"
                          placeholder="Your secret key"
                        />
                      </div>

                      <Button
                        onClick={() => window.open(IDRIVE_PARTNER_URL, '_blank')}
                        variant="link"
                        className="text-[#6D4AFF] p-0 h-auto text-sm"
                      >
                        Don't have an iDrive account? Get one here <ExternalLink className="w-3 h-3 ml-1" />
                      </Button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="ghost" onClick={onClose} className="text-gray-400">
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="bg-[#6D4AFF] hover:bg-[#5B3FD9]"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Settings
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
